import { useMutation } from '@tanstack/react-query';
import { Button, Dropdown, DropdownOption, Modal, TextField } from '@volvo/vcdk-react';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IWifiConfigurationRequest, IWifiNetworkRow } from '@/service/vpc/types/IWifiConfiguration';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { vpcService } from '@/service/vpc/vpcService';

type WiFiFormFields = {
  name: string;
  password: string;
  priority: number;
};

type Props = {
  chassisId: string;
  existingNetworks: IWifiNetworkRow[];
  onMutationSuccess: (correlationId: string, updatedNetworks: IWifiNetworkRow[]) => void;
};

const AddNetworkModal: React.FC<Props> = ({ chassisId, existingNetworks, onMutationSuccess }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // Save button lives in the modal's footer slot, outside the <form>, so submission is triggered imperatively via this ref
  const [formEl, setFormEl] = useState<HTMLFormElement | null>(null);

  const usedPriorities = existingNetworks.map((n) => n.priority);
  const nextFreePriority = [1, 2, 3, 4, 5].find((n) => !usedPriorities.includes(n)) ?? 1;

  const { handleSubmit, reset, control } = useForm<WiFiFormFields>({
    defaultValues: {
      name: '',
      password: '',
      priority: nextFreePriority,
    },
  });

  const { mutate: updateWifiConfiguration, isPending } = useMutation({
    mutationFn: ({ chassisId, configurations }: { chassisId: string; configurations: IWifiConfigurationRequest[] }) => {
      return vpcService.Services.updateWifiConfiguration({ chassisId, configurations });
    },
  });

  const handleOnClose = useCallback(() => {
    reset({
      name: '',
      password: '',
      priority: nextFreePriority,
    });
    setIsOpen(false);
  }, [reset, nextFreePriority]);

  const handleOnSave = useCallback(
    (formdata: WiFiFormFields) => {
      const existingConfigs: IWifiConfigurationRequest[] = existingNetworks.map((n) => ({
        ssid: n.ssid,
        priority: n.priority,
      }));

      const newConfig: IWifiConfigurationRequest = {
        ssid: formdata.name,
        password: formdata.password,
        priority: formdata.priority,
      };

      const updatedNetworks: IWifiNetworkRow[] = [
        ...existingNetworks,
        {
          ssid: formdata.name,
          wifiFrequency: '',
          priority: formdata.priority,
          signalStrength: 0,
          isConnected: false,
          receivedDateTime: new Date().toISOString(),
        },
      ].sort((a, b) => a.priority - b.priority);

      updateWifiConfiguration(
        { chassisId, configurations: [...existingConfigs, newConfig] },
        {
          // Only close the modal and notify the parent once the mutation actually succeeds
          onSuccess: (result) => {
            handleOnClose();
            onMutationSuccess(result.correlationId, updatedNetworks);
          },
        }
      );
    },
    [chassisId, existingNetworks, handleOnClose, onMutationSuccess, updateWifiConfiguration]
  );

  const onSubmit: SubmitHandler<WiFiFormFields> = useCallback(
    (formdata) => {
      handleOnSave(formdata);
    },
    [handleOnSave]
  );

  return (
    <div>
      <Button
        variant="primary"
        size="medium"
        disabled={existingNetworks.length >= 5}
        onClick={(e) => {
          e.preventDefault();
          reset({ name: '', password: '', priority: nextFreePriority });
          setIsOpen(true);
        }}
      >
        {t(($) => $.components.modal.add_network_modal.button)}
      </Button>
      {isOpen &&
        createPortal(
          <Modal open={isOpen} onClose={handleOnClose} maxWidth="90%" width={700}>
            <span slot="header">{t(($) => $.components.modal.add_network_modal.title)}</span>
            <form ref={setFormEl} className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="priority"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Dropdown
                    required
                    label={t(($) => $.components.modal.add_network_modal.priority)}
                    size="medium"
                    className="w-full"
                    value={String(field.value)}
                    placement="bottom"
                    onChange={(e) => field.onChange(Number(e.detail.value))}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <DropdownOption key={n} value={String(n)} disabled={usedPriorities.includes(n)}>
                        {String(n)}
                      </DropdownOption>
                    ))}
                  </Dropdown>
                )}
              />
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    required
                    label={t(($) => $.components.modal.add_network_modal.network_name)}
                    helperText={t(($) => $.components.modal.add_network_modal.network_name_helperText)}
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
              <Controller
                name="password"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    required
                    type="password"
                    label={t(($) => $.components.modal.add_network_modal.password)}
                    helperText={t(($) => $.components.modal.add_network_modal.password_helperText)}
                    value={field.value}
                    onInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </form>
            <span slot="footer">
              <div className="flex gap-4">
                <Button variant="secondary" type="button" onClick={handleOnClose} disabled={isPending}>
                  {t(($) => $.common.cancel)}
                </Button>
                <Button variant="primary" type="button" onClick={() => formEl?.requestSubmit()} disabled={isPending}>
                  {t(($) => $.common.save)}
                </Button>
              </div>
            </span>
          </Modal>,
          document.body
        )}
    </div>
  );
};

export default withComponentErrorBoundary(AddNetworkModal);
